// Запись голосовых сообщений. Одновременно возможна только одна запись,
// поэтому состояние держим в модуле, а не в компоненте.

// Порядок важен: сначала opus (Android/Chrome), потом mp4/aac (iOS/Safari).
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/ogg;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/aac'
];

const PEAKS_TARGET = 40;   // столбиков в волне
const LEVEL_MS = 100;      // как часто снимаем уровень сигнала

let stream = null;
let recorder = null;
let chunks = [];
let audioCtx = null;
let analyser = null;
let levelTimer = null;
let rawPeaks = [];
let startedAt = 0;
let stopPromise = null;
let hitLimit = false;

export function isVoiceSupported() {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
}

function pickMime() {
  if (!window.MediaRecorder || !MediaRecorder.isTypeSupported) return '';
  return MIME_CANDIDATES.find(m => MediaRecorder.isTypeSupported(m)) || '';
}

// Сжимаем снятые уровни до PEAKS_TARGET значений 0..99 для рисунка волны
function normalizePeaks(values) {
  if (!values.length) return [];
  const out = [];
  const step = values.length / PEAKS_TARGET;
  for (let i = 0; i < PEAKS_TARGET; i++) {
    const from = Math.floor(i * step);
    const to = Math.max(from + 1, Math.floor((i + 1) * step));
    let max = 0;
    for (let j = from; j < to && j < values.length; j++) {
      if (values[j] > max) max = values[j];
    }
    out.push(max);
  }
  // Нормируем на самый громкий фрагмент, иначе тихая запись выглядит плоской
  const loudest = Math.max(...out, 0.01);
  return out.map(v => Math.max(4, Math.min(99, Math.round((v / loudest) * 99))));
}

function cleanup() {
  if (levelTimer) { clearInterval(levelTimer); levelTimer = null; }
  if (audioCtx) { audioCtx.close().catch(() => {}); audioCtx = null; }
  analyser = null;
  if (stream) { stream.getTracks().forEach(t => t.stop()); stream = null; }
  recorder = null;
}

/**
 * Начинает запись. Возвращает { mime } либо бросает ошибку (нет доступа к микрофону).
 * onLevel(0..1)  — текущая громкость для живой волны
 * onSecs(number) — секунды записи
 * onLimit()      — запись остановлена сама, дошла до maxSecs
 */
export async function startRecording({ onLevel, onSecs, onLimit, maxSecs = 300 } = {}) {
  if (recorder) throw new Error('Запись уже идёт');

  stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
  });

  const mimeType = pickMime();
  recorder = new MediaRecorder(stream, mimeType ? { mimeType, audioBitsPerSecond: 32000 } : undefined);
  chunks = [];
  rawPeaks = [];
  hitLimit = false;
  startedAt = Date.now();

  recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

  stopPromise = new Promise((resolve) => {
    recorder.onstop = () => {
      const secs = Math.round((Date.now() - startedAt) / 1000);
      const type = (chunks[0] && chunks[0].type) || mimeType || 'audio/webm';
      const blob = new Blob(chunks, { type });
      const peaks = normalizePeaks(rawPeaks);
      cleanup();
      resolve({ blob, secs, peaks, mime: type });
    };
  });

  // Анализатор для живой волны и рисунка волны в отправленном сообщении
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    audioCtx = new Ctx();
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 512;
    audioCtx.createMediaStreamSource(stream).connect(analyser);
  } catch (e) {
    analyser = null;
  }

  const buf = analyser ? new Uint8Array(analyser.fftSize) : null;
  levelTimer = setInterval(() => {
    let level = 0;
    if (analyser && buf) {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      level = Math.min(1, Math.sqrt(sum / buf.length) * 3);
    }
    rawPeaks.push(level);
    if (onLevel) onLevel(level);

    const secs = Math.floor((Date.now() - startedAt) / 1000);
    if (onSecs) onSecs(secs);

    if (secs >= maxSecs && recorder && recorder.state === 'recording') {
      hitLimit = true;
      recorder.stop();
      if (onLimit) onLimit();
    }
  }, LEVEL_MS);

  recorder.start();
  return { mime: recorder.mimeType || mimeType };
}

/** Останавливает запись и отдаёт результат. Безопасно вызывать после автостопа по лимиту. */
export async function stopRecording() {
  if (!stopPromise) return null;
  const p = stopPromise;
  if (recorder && recorder.state === 'recording') recorder.stop();
  const result = await p;
  stopPromise = null;
  return result;
}

/** Отмена: запись выбрасывается, микрофон освобождается. */
export function cancelRecording() {
  if (recorder && recorder.state === 'recording') {
    recorder.onstop = () => cleanup();
    recorder.stop();
  } else {
    cleanup();
  }
  chunks = [];
  rawPeaks = [];
  stopPromise = null;
}

export function isRecording() {
  return !!(recorder && recorder.state === 'recording');
}

export function wasStoppedByLimit() {
  return hitLimit;
}

/** Короткая вибрация — подтверждение жеста на телефоне. */
export function buzz(ms = 20) {
  try { if (navigator.vibrate) navigator.vibrate(ms); } catch (e) {}
}

export function fmtSecs(total) {
  const s = Math.max(0, Math.round(total || 0));
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}
